/* eslint-disable max-lines */
import { useAuth0 } from '@auth0/auth0-react';
import { formatDate, sortAsc } from '@strata/helper-functions';
import { ActionButtonText } from '@strata/react-buttons';
import { ButtonWithDropdownMenu } from '@strata/react-dropdown-menu';
import { FormHeaderAndTabbedContent } from '@strata/react-form-header-and-content';
import { InlineMessage } from '@strata/react-inline-message';
import { FormTitleLabelValuePair, HeaderLabelValuePair } from '@strata/react-label-value-pairs';
import { ClinicalPolicyStatusIndicator } from '@strata/react-status-indicators';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';

import { ApproveInternallyDialog } from 'pages/clinicalPolicy/dialogs/ApproveInternallyDialog';
import { CreateDuplicatePolicyDialog } from 'pages/clinicalPolicy/dialogs/CreateDuplicatePolicyDialog';
import { DraftDeleteDialog } from 'pages/clinicalPolicy/dialogs/DraftDeleteDialog';
import { NextPolicyStatusDialog } from 'pages/clinicalPolicy/dialogs/NextPolicyStatusDialog';
import { RequestChangesDialog } from 'pages/clinicalPolicy/dialogs/RequestChangesDialog';
import { RequestReviewDialog } from 'pages/clinicalPolicy/dialogs/RequestReviewDialog';
import { UpdateDiagnosesDialog } from 'pages/clinicalPolicy/dialogs/UpdateDiagnosesDialog';
import { UpdateEffectiveDateDialog } from 'pages/clinicalPolicy/dialogs/UpdateEffectiveDateDialog';
import { ViewRequestedChangesDialog } from 'pages/clinicalPolicy/dialogs/ViewRequestedChangesDialog';
import { LogicTab } from 'pages/clinicalPolicy/LogicTab';
import { QuestionsTab } from 'pages/clinicalPolicy/QuestionsTab';
import { TestTab } from 'pages/clinicalPolicy/TestTab';
import { handleError } from 'services/ErrorManagement';
import { get, post, put } from 'services/Http';

const getPolicy = async (payerId, draftId) =>
    get(`/clinicalpolicy/admin/${payerId}/drafts/${draftId}`);

// eslint-disable-next-line complexity
export const PolicyEdit = () => {
    const { payerId, draftId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth0();

    const [policy, setPolicy] = useState();
    const fetchPolicy = useCallback(async () => {
        try {
            setPolicy(await getPolicy(payerId, draftId));
        } catch (err) {
            handleError('Error fetching clinical policy.', err);
        }
    }, [payerId, draftId]);
    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        fetchPolicy();
    }, [fetchPolicy]);
    const questions = useMemo(
        () => (policy ? (JSON.parse(policy.graph ?? '{}').nodes ?? []) : undefined),
        [policy],
    );
    const policyStatus = policy?.policy_status?.trim();
    const statusHistory = policy?.status_history;

    const updatePolicyDetails = async (effectiveDate, graph) => {
        // If policy is PEND_PROD or IN_PROD crate new draft & navigate to it
        if (
            policyStatus === ClinicalPolicyStatusIndicator.Statuses.PEND_PROD ||
            policyStatus === ClinicalPolicyStatusIndicator.Statuses.IN_PROD ||
            policyStatus === ClinicalPolicyStatusIndicator.Statuses.RETIRED
        ) {
            const response = await post(
                `/clinicalpolicy/admin/${payerId}/drafts/${policy?.policy_id}`,
                {
                    effective_date: effectiveDate,
                    graph,
                },
            );
            navigate({
                pathname: `/policy-edit/${payerId}/${response.draft_id}`,
                hash: location.hash,
            });
        } else {
            await put(`/clinicalpolicy/admin/${payerId}/drafts/${draftId}`, {
                effective_date: effectiveDate,
                graph,
            });
            await fetchPolicy();
        }
    };

    const updateQuestions = async (nodes) => {
        await updatePolicyDetails(
            policy?.effective_date,
            JSON.stringify({
                product: policy?.product_name,
                diagnosis: policy?.diagnosis_oids,
                nodes,
            }),
        );
    };

    const [showUpdateDiagnosesDialog, setShowUpdateDiagnosesDialog] = useState(false);
    const [showUpdateEffectiveDateDialog, setShowUpdateEffectiveDateDialog] = useState(false);
    const [showDraftDeleteDialog, setShowDraftDeleteDialog] = useState(false);
    const [showRequestReviewDialog, setShowRequestReviewDialog] = useState(false);
    const [showApproveInternallyDialog, setShowApproveInternallyDialog] = useState(false);
    const [showRequestChangesDialog, setShowRequestChangesDialog] = useState(false);
    const [showNextPolicyStatusDialog, setShowNextPolicyStatusDialog] = useState(false);
    const [showViewRequestedChangesDialog, setShowViewRequestedChangesDialog] = useState(false);
    const [showDuplicatePolicyDialog, setShowDuplicatePolicyDialog] = useState(false);

    // Get menu items based on status
    const menuItems = (() => {
        switch (policyStatus) {
            case ClinicalPolicyStatusIndicator.Statuses.DRAFT:
            case ClinicalPolicyStatusIndicator.Statuses.SAVED:
            case ClinicalPolicyStatusIndicator.Statuses.INTCHANGE:
            case ClinicalPolicyStatusIndicator.Statuses.EXTCHANGE:
                return [
                    {
                        text: 'Update Diagnoses',
                        onItemClick: () => setShowUpdateDiagnosesDialog(true),
                    },
                    {
                        text: 'Update Effective Date',
                        onItemClick: () => setShowUpdateEffectiveDateDialog(true),
                    },
                    {
                        text: 'Delete Draft',
                        onItemClick: () => setShowDraftDeleteDialog(true),
                    },
                    {
                        text: 'Request Internal Review',
                        onItemClick: () => setShowRequestReviewDialog(true),
                    },
                ];
            case ClinicalPolicyStatusIndicator.Statuses.INTREVIEW: {
                // User can't review a policy they requested review for
                // TODO DEV-16294 remove true to prevent user from approving own policy
                // eslint-disable-next-line no-constant-condition
                return true ||
                    (user?.email &&
                        user.email !==
                            statusHistory?.find(
                                (change) =>
                                    change.policy_status ===
                                    ClinicalPolicyStatusIndicator.Statuses.INTREVIEW,
                            )?.username)
                    ? [
                          {
                              text: 'Approve Internally',
                              onItemClick: () => setShowApproveInternallyDialog(true),
                          },
                          {
                              text: 'Request Changes',
                              onItemClick: () => setShowRequestChangesDialog(true),
                          },
                      ]
                    : [];
            }
            case ClinicalPolicyStatusIndicator.Statuses.INTAPPROVE:
                return [
                    {
                        text: 'Request External Review',
                        onItemClick: () => setShowRequestReviewDialog(true),
                    },
                    {
                        text: 'Request Changes',
                        onItemClick: () => setShowRequestChangesDialog(true),
                    },
                    {
                        text: 'Move to Pending Production',
                        onItemClick: () => setShowNextPolicyStatusDialog(true),
                    },
                ];
            case ClinicalPolicyStatusIndicator.Statuses.EXTAPPROVE:
                return [
                    {
                        text: 'Move to Pending Production',
                        onItemClick: () => setShowNextPolicyStatusDialog(true),
                    },
                ];
            case ClinicalPolicyStatusIndicator.Statuses.PEND_PROD:
                return [
                    {
                        text: 'Send Back to Draft Status',
                        onItemClick: () => setShowNextPolicyStatusDialog(true),
                    },
                ];
            case ClinicalPolicyStatusIndicator.Statuses.IN_PROD:
                return [
                    {
                        text: 'Deactivate Policy',
                        onItemClick: () => setShowNextPolicyStatusDialog(true),
                    },
                ];
            default:
                return [];
        }
    })();

    if (policy?.change_requests?.length > 0) {
        menuItems.push({
            text: 'View Requested Changes',
            onItemClick: () => setShowViewRequestedChangesDialog(true),
        });
    }

    const additionalMenuItems = [
        { text: 'Duplicate Policy', onItemClick: () => setShowDuplicatePolicyDialog(true) },
    ];

    // Can't edit policy when in review or approved state
    const readOnly =
        policyStatus === ClinicalPolicyStatusIndicator.Statuses.INTREVIEW ||
        policyStatus === ClinicalPolicyStatusIndicator.Statuses.INTAPPROVE ||
        policyStatus === ClinicalPolicyStatusIndicator.Statuses.EXTREVIEW ||
        policyStatus === ClinicalPolicyStatusIndicator.Statuses.EXTAPPROVE;

    return (
        <>
            <UpdateDiagnosesDialog
                isOpen={showUpdateDiagnosesDialog}
                fetchPolicy={fetchPolicy}
                onSecondaryButtonClick={() => setShowUpdateDiagnosesDialog(false)}
                diagnoses={policy?.diagnosis_oids}
                payerId={payerId}
                draftId={draftId}
            />
            <UpdateEffectiveDateDialog
                isOpen={showUpdateEffectiveDateDialog}
                onSubmit={(newDate) => updatePolicyDetails(newDate, policy?.graph)}
                onSecondaryButtonClick={() => setShowUpdateEffectiveDateDialog(false)}
                effectiveDate={policy?.effective_date}
            />
            <DraftDeleteDialog
                isOpen={showDraftDeleteDialog}
                onSecondaryButtonClick={() => setShowDraftDeleteDialog(false)}
                payerId={payerId}
                draftId={draftId}
            />
            <RequestReviewDialog
                isOpen={showRequestReviewDialog}
                isExternal={policyStatus === ClinicalPolicyStatusIndicator.Statuses.INTAPPROVE}
                fetchPolicy={fetchPolicy}
                onSecondaryButtonClick={() => setShowRequestReviewDialog(false)}
                statusHistory={statusHistory}
                payerId={payerId}
                draftId={draftId}
            />
            <ApproveInternallyDialog
                isOpen={showApproveInternallyDialog}
                fetchPolicy={fetchPolicy}
                onSecondaryButtonClick={() => setShowApproveInternallyDialog(false)}
                statusHistory={statusHistory}
                payerId={payerId}
                draftId={draftId}
            />
            <RequestChangesDialog
                isOpen={showRequestChangesDialog}
                fetchPolicy={fetchPolicy}
                onSecondaryButtonClick={() => setShowRequestChangesDialog(false)}
                payerId={payerId}
                draftId={draftId}
            />
            <NextPolicyStatusDialog
                isOpen={showNextPolicyStatusDialog}
                fetchPolicy={fetchPolicy}
                onSecondaryButtonClick={() => setShowNextPolicyStatusDialog(false)}
                currentStatus={policyStatus}
                payerId={payerId}
                draftId={draftId}
            />
            <ViewRequestedChangesDialog
                isOpen={showViewRequestedChangesDialog}
                onClose={() => setShowViewRequestedChangesDialog(false)}
                changeRequests={policy?.change_requests}
                payerId={payerId}
                draftId={draftId}
            />
            <CreateDuplicatePolicyDialog
                isOpen={showDuplicatePolicyDialog}
                onSecondaryButtonClick={() => setShowDuplicatePolicyDialog(false)}
                payerId={payerId}
                sourceDraftId={draftId}
            />
            <FormHeaderAndTabbedContent
                rowOneContentHeader={
                    <>
                        <ActionButtonText
                            label="Back To Clinical Policies"
                            isBackNavigationButton
                            onClick={() => navigate(`/policy-list/${payerId}`)}
                        />
                        <div style={{ marginLeft: 'auto' }}>
                            <ButtonWithDropdownMenu
                                menuItems={menuItems}
                                additionalMenuItems={additionalMenuItems}
                                intent={ButtonWithDropdownMenu.Intent.PRIMARY}
                                label="Actions"
                            />
                        </div>
                    </>
                }
                rowTwoContentHeader={
                    policy && (
                        <>
                            <FormTitleLabelValuePair
                                label={policy.product_name}
                                value={policy.diagnosis_oids?.sort(sortAsc).join(', ')}
                            />
                            <HeaderLabelValuePair
                                label="Status"
                                value={<ClinicalPolicyStatusIndicator status={policyStatus} />}
                            />
                            <HeaderLabelValuePair
                                label="Effective Date"
                                value={formatDate(policy.effective_date)}
                            />
                            {statusHistory?.[0]?.assigned_to && (
                                <HeaderLabelValuePair
                                    label="Assigned To"
                                    value={statusHistory[0].assigned_to}
                                />
                            )}
                        </>
                    )
                }
                rowThreeContentHeader={
                    (policyStatus === ClinicalPolicyStatusIndicator.Statuses.PEND_PROD ||
                        policyStatus === ClinicalPolicyStatusIndicator.Statuses.IN_PROD) && (
                        <InlineMessage
                            intent={InlineMessage.Intent.INFO}
                            label={`Editing ${
                                policyStatus === ClinicalPolicyStatusIndicator.Statuses.PEND_PROD
                                    ? 'a Pending'
                                    : 'an In'
                            } Production policy will not make changes in production. A new Draft policy will be created and will need to go through the normal approval process.`}
                        />
                    )
                }
                activeTab={location.hash.replace(/^#/, '')}
                onTabSelect={(tab) => navigate({ ...location, hash: tab })}
                tabs={[
                    {
                        title: 'questions',
                        mainContent: (
                            <QuestionsTab
                                questions={questions}
                                updateQuestions={updateQuestions}
                                readOnly={readOnly}
                                payerId={payerId}
                            />
                        ),
                    },
                    {
                        title: 'logic',
                        mainContent: (
                            <LogicTab
                                questions={questions}
                                updateQuestions={updateQuestions}
                                readOnly={readOnly}
                            />
                        ),
                    },
                    {
                        title: 'test',
                        mainContent: <TestTab questions={questions} />,
                    },
                ]}
            />
        </>
    );
};
